import {
  setBearerToken,
  setBearerTokenCookie,
  setLandlordBearerToken,
  setRefreshToken,
} from '@afinius/api-client/bearer-token';
import { afterResponseHooks } from '@afinius/api-client/client';
import { landlordAuthApi } from '@afinius/api-client/landlord-auth/api';
import type {
  LoginRequest,
  LoginResponse,
  MsalInitResponse,
} from '@afinius/api-client/landlord-auth/types';
import type { LandlordTenant } from '@afinius/api-client/landlord-tenant/types';
import { queryClient } from '@afinius/plugins/query';
import { msalMobileLogin } from '@afinius/utilities/msal-mobile';
import {
  isMsalPopupBlockedError,
  msalWebHandleRedirect,
  msalWebLogin,
  msalWebLoginRedirect,
} from '@afinius/utilities/msal-web';
import { refreshExpirationMinutes } from '@afinius/utilities/silent-sso';
import { Capacitor } from '@capacitor/core';
import { defineStore } from 'pinia';
import { type Ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import { useLocalStorage } from '../useLocalStorage';

const LOGOUT_TIMEOUT_MS = 30_000;

interface Store {
  isAuthenticated: Ref<boolean>;
  hasAcceptedTerms: Ref<boolean>;
  hasTenantAccess: Ref<boolean>;
  forceTenant: Ref<LandlordTenant | null>;
  login: (data: LoginRequest) => Promise<LoginResponse>;
  ssoVerify: (token: string) => Promise<LoginResponse>;
  msalLogin: (init: MsalInitResponse) => Promise<LoginResponse>;
  msalCompleteRedirect: () => Promise<LoginResponse | null>;
  msalSwitch: (password: string, init: MsalInitResponse) => Promise<void>;
  logout: (invalidated?: boolean) => Promise<void>;
  clear: () => void;
  reset: () => Promise<void>;
  setHasAcceptedTerms: (value: boolean) => void;
  setHasTenantAccess: (value: boolean) => void;
  setForceTenant: (value: LandlordTenant | null) => void;
}

export const useLandlordAuthStore = defineStore('landlord-auth', (): Store => {
  const route = useRoute();
  const router = useRouter();

  const [isAuthenticated, setIsAuthenticated] = useLocalStorage('is-authenticated', false);
  const [hasAcceptedTerms, setHasAcceptedTerms] = useLocalStorage('has-accepted-terms', false);
  const [hasTenantAccess, setHasTenantAccess] = useLocalStorage('has-tenant-access', false);
  const [forceTenant, setForceTenant] = useLocalStorage<LandlordTenant | null>(
    'force-tenant',
    null,
  );

  const loginSuccess = async (response: LoginResponse) => {
    if (response.access_token) {
      setIsAuthenticated(true);
      setLandlordBearerToken(`Bearer ${response.access_token}`, response.expires_in);
      setBearerTokenCookie(`Bearer ${response.access_token}`);
    }

    if (response.refresh_token) {
      setRefreshToken(response.refresh_token, response.refresh_expires_in);
    }

    setHasAcceptedTerms(response.has_accepted_terms);
    setHasTenantAccess(response.has_tenant_access);
    setForceTenant(response.force_tenant);

    return response;
  };

  const login = async (data: LoginRequest) => {
    return landlordAuthApi
      .login({ ...data, refresh_expiration: refreshExpirationMinutes() })
      .then(loginSuccess)
      .catch((error) => {
        throw error;
      });
  };

  const clear = () => {
    queryClient.clear();

    setIsAuthenticated(false);
    setLandlordBearerToken(undefined);
    setBearerToken(undefined);
    setBearerTokenCookie(undefined);
    setRefreshToken(undefined);
    setHasAcceptedTerms(false);
    setHasTenantAccess(false);
    setForceTenant(null);
  };

  const reset = async () => {
    clear();

    if (router.hasRoute('nodr.landing')) {
      await router.push({ name: 'nodr.landing' });
    } else if (router.hasRoute('auth.login')) {
      await router.push({ name: 'auth.login' });
    }
  };

  const ssoVerify = async (token: string) => {
    return landlordAuthApi
      .ssoVerify({ token, refresh_expiration: refreshExpirationMinutes() })
      .then(loginSuccess)
      .catch((error) => {
        throw error;
      });
  };

  const logout = async (invalidated = false) => {
    if (invalidated) {
      await reset();

      return;
    }

    try {
      const timeoutPromise = new Promise((r) => {
        setTimeout(r, LOGOUT_TIMEOUT_MS);
      });

      await Promise.race([landlordAuthApi.logout(), timeoutPromise]);
    } catch {
      // Ensures local state is reset below
    }

    await reset();
  };

  const msalTokenLogin = (tokens: { idToken: string; accessToken: string }, nonceId: string) =>
    landlordAuthApi.msalTokenLogin({
      id_token: tokens.idToken,
      access_token: tokens.accessToken,
      nonce_id: nonceId,
      refresh_expiration: refreshExpirationMinutes(),
    });

  const completeMsalLogin = async (perform: () => Promise<LoginResponse>) => {
    try {
      const response = await perform();
      return loginSuccess(response);
    } catch (error) {
      const apiError = error as {
        response?: {
          status?: number;
          data?: { link_required?: boolean; email?: string };
        };
      };
      const status = apiError.response?.status;
      const data = apiError.response?.data;

      if (status === 409 && data?.link_required && data.email) {
        router.push({
          name: 'auth.switch-to-sso',
          query: { email: data.email },
        });
      }

      throw error;
    }
  };

  const msalLogin = ({ nonce_id, nonce }: MsalInitResponse) => {
    if (Capacitor.isNativePlatform()) {
      return completeMsalLogin(async () => {
        const { code, codeVerifier, state, redirectUri } = await msalMobileLogin(nonce);
        return landlordAuthApi.msalCodeLogin({
          code,
          code_verifier: codeVerifier,
          nonce_id,
          state,
          redirect_uri: redirectUri,
          refresh_expiration: refreshExpirationMinutes(),
        });
      });
    }

    return completeMsalLogin(async () => {
      const tokens = await msalWebLogin(nonce).catch((error) => {
        if (isMsalPopupBlockedError(error)) {
          return msalWebLoginRedirect(nonce, nonce_id);
        }

        throw error;
      });

      return msalTokenLogin(tokens, nonce_id);
    });
  };

  const msalCompleteRedirect = async () => {
    if (Capacitor.isNativePlatform()) {
      return null;
    }

    const result = await msalWebHandleRedirect();

    if (!result) {
      return null;
    }

    return completeMsalLogin(() => msalTokenLogin(result, result.state));
  };

  const msalSwitch = async (password: string, { nonce_id, nonce }: MsalInitResponse) => {
    if (Capacitor.isNativePlatform()) {
      const { code, codeVerifier, state, redirectUri } = await msalMobileLogin(nonce);
      await landlordAuthApi.msalSwitchCode({
        code,
        code_verifier: codeVerifier,
        nonce_id,
        state,
        redirect_uri: redirectUri,
        password,
      });
      return;
    }

    const { idToken, accessToken } = await msalWebLogin(nonce);
    await landlordAuthApi.msalSwitchToken({
      id_token: idToken,
      access_token: accessToken,
      nonce_id,
      password,
    });
  };

  afterResponseHooks.push(async ({ response }) => {
    if (response.status === 503) {
      router.push({ name: 'maintenance' });
    }

    if (response.status === 451 && route.name !== 'auth.terms') {
      router.push({ name: 'auth.terms' });
    }

    return response;
  });

  return {
    isAuthenticated,
    hasAcceptedTerms,
    hasTenantAccess,
    forceTenant,
    login,
    ssoVerify,
    msalLogin,
    msalCompleteRedirect,
    msalSwitch,
    logout,
    clear,
    reset,
    setHasAcceptedTerms,
    setHasTenantAccess,
    setForceTenant,
  };
});
